import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  GitBranch, Plus, Play, Save, Trash2, Copy, ChevronDown, ChevronUp,
  Calendar, Users, AlertTriangle, TrendingUp, Target, Loader2,
  Settings2, BarChart3, CheckCircle2, XCircle, ArrowRight, Sparkles,
  RefreshCw, ExternalLink, Info, Layers, Clock
} from 'lucide-react';
import { api } from '../api/client';
import { useTenant } from '../context/TenantContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// ==========================================
// CONSTANTS
// ==========================================
const PRIORITY_LEVELS = [1, 2, 3, 4, 5];
const PRIORITY_LABELS = {
  1: 'Critical',
  2: 'High',
  3: 'Medium',
  4: 'Low',
  5: 'Optional'
};

// ==========================================
// DEMAND VS CAPACITY CHART
// ==========================================
const DemandCapacityChart = ({ demandByMonth, capacityByMonth, height = 200 }) => {
  const months = [...new Set([
    ...Object.keys(demandByMonth || {}),
    ...Object.keys(capacityByMonth || {})
  ])].sort().slice(0, 8);

  if (months.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-400">
        No data to display
      </div>
    );
  }

  const maxValue = Math.max(
    ...months.map(m => Math.max(demandByMonth?.[m] || 0, capacityByMonth?.[m] || 0, 100))
  );

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-2" style={{ height }}>
        {months.map((month) => {
          const demand = demandByMonth?.[month] || 0;
          const capacity = capacityByMonth?.[month] || 0;
          const demandHeight = Math.max((demand / maxValue) * 100, 2);
          const capacityHeight = Math.max((capacity / maxValue) * 100, 2);
          const isOverload = demand > capacity;

          return (
            <div key={month} className="flex-1 flex flex-col items-center gap-1">
              <div className="flex items-end gap-0.5 w-full h-full">
                <div
                  className={`flex-1 rounded-t transition-all ${isOverload ? 'bg-red-500' : 'bg-purple-500'}`}
                  style={{ height: `${demandHeight}%` }}
                  title={`Demand: ${demand}h`}
                />
                <div
                  className="flex-1 bg-green-500 rounded-t transition-all opacity-60"
                  style={{ height: `${capacityHeight}%` }}
                  title={`Capacity: ${capacity}h`}
                />
              </div>
              <span className="text-[10px] text-gray-500">{month.slice(5)}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center justify-center gap-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-purple-500 rounded" />
          <span className="text-gray-600">Demand</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded opacity-60" />
          <span className="text-gray-600">Capacity</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-500 rounded" />
          <span className="text-gray-600">Overload</span>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// BOTTLENECK TABLE
// ==========================================
const BottleneckTable = ({ bottlenecks }) => {
  const teamBottlenecks = bottlenecks?.byTeam || {};
  const months = Object.keys(teamBottlenecks).sort();

  if (months.length === 0) {
    return (
      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">No bottlenecks detected</span>
        </div>
        <p className="text-sm text-green-600 mt-1">
          Resource capacity is sufficient for planned demand
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 font-medium text-gray-700">Month</th>
            <th className="text-right py-2 px-3 font-medium text-gray-700">Demand</th>
            <th className="text-right py-2 px-3 font-medium text-gray-700">Capacity</th>
            <th className="text-right py-2 px-3 font-medium text-gray-700">Gap</th>
            <th className="text-center py-2 px-3 font-medium text-gray-700">Severity</th>
          </tr>
        </thead>
        <tbody>
          {months.map(month => {
            const b = teamBottlenecks[month];
            const severityColors = {
              warning: 'bg-amber-100 text-amber-700',
              critical: 'bg-orange-100 text-orange-700',
              severe: 'bg-red-100 text-red-700'
            };
            return (
              <tr key={month} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium">{month}</td>
                <td className="py-2 px-3 text-right">{b.demand}h</td>
                <td className="py-2 px-3 text-right">{b.capacity}h</td>
                <td className="py-2 px-3 text-right text-red-600">+{b.gap}h</td>
                <td className="py-2 px-3 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${severityColors[b.severity]}`}>
                    {b.severity}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ==========================================
// OVERLOADED PROJECTS LIST
// ==========================================
const OverloadedProjectsList = ({ projects, onExclude }) => {
  if (!projects || projects.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-sm">
        No overloaded projects identified
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {projects.slice(0, 5).map((proj, idx) => {
        const severityColors = {
          high: 'border-red-200 bg-red-50',
          medium: 'border-amber-200 bg-amber-50',
          low: 'border-gray-200 bg-gray-50'
        };
        return (
          <div 
            key={proj.projectId || idx}
            className={`p-3 rounded-lg border ${severityColors[proj.severity]}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-900">{proj.projectName}</span>
                <p className="text-xs text-gray-600 mt-0.5">
                  Contributing {proj.contributingDemand}h in bottleneck months
                </p>
              </div>
              {onExclude && (
                <button
                  onClick={() => onExclude(proj.projectId)}
                  className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-100"
                >
                  Exclude
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>Affected months: {proj.affectedMonths?.join(', ')}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==========================================
// PROJECT CONTROL CARD
// ==========================================
const ProjectControlCard = ({ 
  project, 
  override, 
  onToggleInclude, 
  onUpdateOverride 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isIncluded = override?.includeFlag !== false;

  return (
    <div className={`border rounded-lg ${isIncluded ? 'border-gray-200' : 'border-gray-300 bg-gray-50 opacity-60'}`}>
      <div className="p-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={isIncluded}
            onChange={() => onToggleInclude(project.id)}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <div>
            <span className={`font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-500'}`}>
              {project.name}
            </span>
            {override?.startDate && (
              <span className="ml-2 text-xs text-purple-600">
                (shifted)
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1 hover:bg-gray-100 rounded"
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </button>
      </div>

      {isExpanded && isIncluded && (
        <div className="px-3 pb-3 pt-2 border-t border-gray-100 space-y-3">
          {/* Date Override */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Start Date Override</label>
              <input
                type="date"
                value={override?.startDate || ''}
                onChange={(e) => onUpdateOverride(project.id, 'startDate', e.target.value)}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-200 rounded"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600">End Date Override</label>
              <input
                type="date"
                value={override?.endDate || ''}
                onChange={(e) => onUpdateOverride(project.id, 'endDate', e.target.value)}
                className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-200 rounded"
              />
            </div>
          </div>

          {/* Priority Override */}
          <div>
            <label className="text-xs text-gray-600">Priority Override</label>
            <select
              value={override?.priority || ''}
              onChange={(e) => onUpdateOverride(project.id, 'priority', parseInt(e.target.value) || null)}
              className="w-full mt-1 px-2 py-1.5 text-sm border border-gray-200 rounded"
            >
              <option value="">No change</option>
              {PRIORITY_LEVELS.map(p => (
                <option key={p} value={p}>{p} - {PRIORITY_LABELS[p]}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// AI RECOMMENDATION PANEL
// ==========================================
const AIRecommendationPanel = ({ scenarioId, tenantId }) => {
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState(null);

  const fetchRecommendations = async () => {
    if (!scenarioId || !tenantId) return;
    
    setLoading(true);
    try {
      const response = await api.getScenarioRecommendations(scenarioId, tenantId);
      setRecommendations(response.data.recommendations || []);
    } catch (err) {
      console.error('Failed to get recommendations:', err);
      toast.error('Failed to load recommendations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scenarioId) {
      fetchRecommendations();
    }
  }, [scenarioId]);

  const severityColors = {
    high: 'border-red-200 bg-red-50',
    medium: 'border-amber-200 bg-amber-50',
    low: 'border-blue-200 bg-blue-50'
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          <h3 className="font-semibold text-gray-900">AI Recommendations</h3>
        </div>
        <button
          onClick={fetchRecommendations}
          disabled={loading || !scenarioId}
          className="p-1.5 hover:bg-gray-100 rounded-lg disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {!scenarioId ? (
        <div className="p-4 bg-gray-50 rounded-lg text-gray-500 text-sm">
          Save scenario first to get recommendations
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
        </div>
      ) : recommendations?.length > 0 ? (
        <div className="space-y-3">
          {recommendations.map((rec, idx) => (
            <div 
              key={idx}
              className={`p-3 rounded-lg border ${severityColors[rec.severity]}`}
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                  rec.severity === 'high' ? 'text-red-600' :
                  rec.severity === 'medium' ? 'text-amber-600' : 'text-blue-600'
                }`} />
                <div>
                  <span className="font-medium text-gray-900 text-sm">{rec.title}</span>
                  <p className="text-xs text-gray-600 mt-1">{rec.description}</p>
                  <p className="text-xs text-purple-700 mt-2 font-medium">{rec.suggestion}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">Scenario looks balanced</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN SCENARIO PLANNING PAGE
// ==========================================
const ScenarioPlanningPage = () => {
  const navigate = useNavigate();
  const { scenarioId: urlScenarioId } = useParams();
  const { tenant, loading: tenantLoading, activeTenantId } = useTenant();
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState(urlScenarioId || null);
  const [scenarioData, setScenarioData] = useState({
    name: 'New Scenario',
    description: '',
    projects: [],
    projectOverrides: {},
    capacityOverrides: {}
  });
  const [calculationResult, setCalculationResult] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // TENANT ISOLATION: Use activeTenantId from context which respects impersonation
  const effectiveTenantId = activeTenantId || user?.tenant_id || user?.tenant?.id;
  
  // Check if contexts are ready
  const contextsReady = !authLoading && !tenantLoading && isAuthenticated;

  // Load initial data when contexts are ready
  useEffect(() => {
    // Skip if data already loaded or contexts not ready
    if (dataLoaded) return;
    
    // Wait for both auth and tenant to finish loading
    if (authLoading || tenantLoading) {
      return;
    }
    
    // User must be authenticated
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    
    // TENANT ISOLATION: Use effectiveTenantId which respects impersonation
    const tenantId = effectiveTenantId;
    
    console.log('ScenarioPlanningPage: Loading data', {
      tenantId,
      userId: user?.id,
      contextsReady
    });
    
    if (tenantId) {
      loadDataWithTenant(tenantId);
    } else {
      console.warn('No tenant ID available');
      setLoading(false);
    }
  }, [authLoading, tenantLoading, isAuthenticated, user, tenant, dataLoaded]);

  // Load specific scenario if in URL (after data is loaded)
  useEffect(() => {
    if (urlScenarioId && effectiveTenantId && dataLoaded) {
      loadScenario(urlScenarioId);
    }
  }, [urlScenarioId, effectiveTenantId, dataLoaded]);

  const loadDataWithTenant = async (tid) => {
    console.log('loadDataWithTenant called with tenant:', tid);
    setLoading(true);
    try {
      const [scenariosRes, projectsRes] = await Promise.all([
        api.listScenarios(tid),
        api.getProjects()
      ]);
      
      const loadedProjects = projectsRes.data.projects || [];
      const loadedScenarios = scenariosRes.data.scenarios || [];
      
      console.log('Data loaded:', {
        projectsCount: loadedProjects.length,
        scenariosCount: loadedScenarios.length,
        projectNames: loadedProjects.map(p => p.name)
      });
      
      setScenarios(loadedScenarios);
      setProjects(loadedProjects);
      setDataLoaded(true);
      
      // Auto-select all projects for new scenario
      if (!urlScenarioId && loadedProjects.length > 0) {
        const allProjectIds = loadedProjects.map(p => p.id);
        setScenarioData(prev => ({
          ...prev,
          projects: allProjectIds
        }));
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load scenario data');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    // TENANT ISOLATION: Use effectiveTenantId which respects impersonation
    const tid = effectiveTenantId;
    if (!tid) return;
    await loadDataWithTenant(tid);
  };

  const loadScenario = async (id) => {
    try {
      const response = await api.getScenario(id, effectiveTenantId);
      const scenario = response.data.scenario;
      
      setScenarioData({
        name: scenario.name,
        description: scenario.description || '',
        projects: scenario.projects || [],
        projectOverrides: scenario.projectOverrides || {},
        capacityOverrides: scenario.capacityOverrides || {}
      });
      setSelectedScenarioId(id);
      setHasUnsavedChanges(false);
      
      // Auto-calculate
      await calculateScenario(id);
    } catch (err) {
      console.error('Failed to load scenario:', err);
      toast.error('Failed to load scenario');
    }
  };

  const calculateScenario = async (id) => {
    if (!id) return;
    
    setCalculating(true);
    try {
      const response = await api.calculateScenario(id, effectiveTenantId, true);
      setCalculationResult(response.data);
    } catch (err) {
      console.error('Failed to calculate scenario:', err);
      toast.error('Failed to calculate scenario');
    } finally {
      setCalculating(false);
    }
  };

  const saveScenario = async () => {
    if (!effectiveTenantId) return;
    
    setSaving(true);
    try {
      if (selectedScenarioId) {
        // Update existing
        await api.updateScenario(selectedScenarioId, {
          name: scenarioData.name,
          description: scenarioData.description,
          projects: scenarioData.projects,
          projectOverrides: scenarioData.projectOverrides,
          capacityOverrides: scenarioData.capacityOverrides
        }, effectiveTenantId);
        toast.success('Scenario updated');
      } else {
        // Create new
        const response = await api.createScenario(scenarioData, effectiveTenantId, user?.id);
        const newId = response.data.scenario.id;
        setSelectedScenarioId(newId);
        navigate(`/scenarios/${newId}`, { replace: true });
        toast.success('Scenario created');
      }
      
      setHasUnsavedChanges(false);
      await loadData();
      
      // Recalculate
      if (selectedScenarioId) {
        await calculateScenario(selectedScenarioId);
      }
    } catch (err) {
      console.error('Failed to save scenario:', err);
      toast.error('Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const deleteScenario = async () => {
    if (!selectedScenarioId || !effectiveTenantId) return;
    
    if (!window.confirm('Delete this scenario?')) return;
    
    try {
      await api.deleteScenario(selectedScenarioId, effectiveTenantId);
      toast.success('Scenario deleted');
      setSelectedScenarioId(null);
      setScenarioData({
        name: 'New Scenario',
        description: '',
        projects: projects.map(p => p.id),
        projectOverrides: {},
        capacityOverrides: {}
      });
      setCalculationResult(null);
      navigate('/scenarios');
      await loadData();
    } catch (err) {
      console.error('Failed to delete scenario:', err);
      toast.error('Failed to delete scenario');
    }
  };

  const duplicateScenario = async () => {
    if (!selectedScenarioId || !effectiveTenantId) return;
    
    try {
      const response = await api.duplicateScenario(selectedScenarioId, effectiveTenantId);
      const newId = response.data.scenario.id;
      toast.success('Scenario duplicated');
      navigate(`/scenarios/${newId}`);
      await loadData();
    } catch (err) {
      console.error('Failed to duplicate scenario:', err);
      toast.error('Failed to duplicate scenario');
    }
  };

  const handleToggleProjectInclude = (projectId) => {
    setScenarioData(prev => {
      const overrides = { ...prev.projectOverrides };
      if (!overrides[projectId]) {
        overrides[projectId] = { includeFlag: false };
      } else {
        overrides[projectId] = {
          ...overrides[projectId],
          includeFlag: !overrides[projectId].includeFlag
        };
      }
      return { ...prev, projectOverrides: overrides };
    });
    setHasUnsavedChanges(true);
  };

  const handleUpdateProjectOverride = (projectId, field, value) => {
    setScenarioData(prev => {
      const overrides = { ...prev.projectOverrides };
      if (!overrides[projectId]) {
        overrides[projectId] = { includeFlag: true };
      }
      overrides[projectId][field] = value;
      return { ...prev, projectOverrides: overrides };
    });
    setHasUnsavedChanges(true);
  };

  const handleExcludeProject = (projectId) => {
    handleUpdateProjectOverride(projectId, 'includeFlag', false);
  };

  const handleNewScenario = () => {
    setSelectedScenarioId(null);
    setScenarioData({
      name: 'New Scenario',
      description: '',
      projects: projects.map(p => p.id),
      projectOverrides: {},
      capacityOverrides: {}
    });
    setCalculationResult(null);
    setHasUnsavedChanges(false);
    navigate('/scenarios');
  };

  const handleQuickSimulate = async () => {
    setCalculating(true);
    try {
      const response = await api.quickSimulate({
        projects: scenarioData.projects,
        projectOverrides: scenarioData.projectOverrides,
        capacityOverrides: scenarioData.capacityOverrides
      }, effectiveTenantId);
      
      // Convert quick simulate result to match calculation result format
      setCalculationResult({
        demand: { byTeamMonthly: response.data.demandByMonth },
        capacity: { byTeamMonthly: response.data.capacityByMonth },
        bottlenecks: { byTeam: {}, totalBottleneckMonths: response.data.bottleneckCount },
        overloadedProjects: response.data.overloadedProjects,
        summary: {
          totalDemand: response.data.demandTotal,
          totalCapacity: response.data.capacityTotal,
          bottleneckCount: response.data.bottleneckCount
        }
      });
    } catch (err) {
      console.error('Quick simulate failed:', err);
      toast.error('Simulation failed');
    } finally {
      setCalculating(false);
    }
  };

  // Show loading state while contexts are initializing or data is loading
  if (authLoading || tenantLoading || (loading && !dataLoaded)) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-purple-600 animate-spin mx-auto" />
          <p className="text-sm text-gray-500 mt-2">Loading scenario planning...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="scenario-planning-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-xl">
              <GitBranch className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Scenario Planning</h1>
              <p className="text-sm text-gray-500">Portfolio "what-if" analysis</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleNewScenario}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              data-testid="new-scenario-btn"
            >
              <Plus className="w-4 h-4" />
              New Scenario
            </button>
            
            {selectedScenarioId && (
              <>
                <button
                  onClick={duplicateScenario}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  data-testid="duplicate-scenario-btn"
                >
                  <Copy className="w-4 h-4" />
                  Duplicate
                </button>
                <button
                  onClick={deleteScenario}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                  data-testid="delete-scenario-btn"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}
            
            <button
              onClick={saveScenario}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              data-testid="save-scenario-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {hasUnsavedChanges ? 'Save Changes' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - 3 Column Layout */}
      <div className="grid grid-cols-12 gap-6 p-6">
        {/* LEFT PANEL: Scenario Controls */}
        <div className="col-span-3 space-y-4">
          {/* Scenario Selector */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <label className="text-sm font-medium text-gray-700">Select Scenario</label>
            <select
              value={selectedScenarioId || ''}
              onChange={(e) => e.target.value ? loadScenario(e.target.value) : handleNewScenario()}
              className="w-full mt-2 px-3 py-2 border border-gray-200 rounded-lg"
              data-testid="scenario-selector"
            >
              <option value="">+ New Scenario</option>
              {scenarios.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Scenario Details */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Scenario Name</label>
              <input
                type="text"
                value={scenarioData.name}
                onChange={(e) => {
                  setScenarioData(prev => ({ ...prev, name: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg"
                data-testid="scenario-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={scenarioData.description}
                onChange={(e) => {
                  setScenarioData(prev => ({ ...prev, description: e.target.value }));
                  setHasUnsavedChanges(true);
                }}
                rows={2}
                className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                placeholder="Describe the scenario..."
              />
            </div>
          </div>

          {/* Project Selection */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <span className="font-medium text-gray-900">Projects</span>
              <span className="text-xs text-gray-500" data-testid="projects-count">
                {dataLoaded ? (
                  `${projects.filter(p => scenarioData.projectOverrides[p.id]?.includeFlag !== false).length}/${projects.length} included`
                ) : (
                  'Loading...'
                )}
              </span>
            </div>
            <div className="p-3 space-y-2 max-h-80 overflow-y-auto" data-testid="projects-list">
              {!dataLoaded ? (
                <div className="flex flex-col items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-purple-600 animate-spin mb-2" />
                  <span className="text-sm text-gray-500">Loading projects...</span>
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-4 text-gray-500 text-sm" data-testid="no-projects-message">
                  No projects available. <br />
                  <span className="text-xs">Create projects first or seed demo data.</span>
                </div>
              ) : (
                projects.map(project => (
                  <ProjectControlCard
                    key={project.id}
                    project={project}
                    override={scenarioData.projectOverrides[project.id]}
                    onToggleInclude={handleToggleProjectInclude}
                    onUpdateOverride={handleUpdateProjectOverride}
                  />
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <button
              onClick={handleQuickSimulate}
              disabled={calculating}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50"
              data-testid="simulate-btn"
            >
              {calculating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {calculating ? 'Calculating...' : 'Run Simulation'}
            </button>
          </div>
        </div>

        {/* CENTER PANEL: Results */}
        <div className="col-span-6 space-y-4">
          {/* Summary Cards */}
          {calculationResult && (
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs">Total Demand</span>
                </div>
                <span className="text-2xl font-bold text-purple-600">
                  {calculationResult.summary?.totalDemand || 0}h
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Users className="w-4 h-4" />
                  <span className="text-xs">Total Capacity</span>
                </div>
                <span className="text-2xl font-bold text-green-600">
                  {calculationResult.summary?.totalCapacity || 0}h
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Target className="w-4 h-4" />
                  <span className="text-xs">Utilization</span>
                </div>
                <span className={`text-2xl font-bold ${
                  (calculationResult.summary?.utilizationPercent || 0) > 100 ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {calculationResult.summary?.utilizationPercent || 0}%
                </span>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs">Bottlenecks</span>
                </div>
                <span className={`text-2xl font-bold ${
                  (calculationResult.bottlenecks?.totalBottleneckMonths || 0) > 0 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {calculationResult.bottlenecks?.totalBottleneckMonths || 0}
                </span>
              </div>
            </div>
          )}

          {/* Demand vs Capacity Chart */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Demand vs Capacity</h3>
            </div>
            {calculationResult ? (
              <DemandCapacityChart
                demandByMonth={calculationResult.demand?.byTeamMonthly}
                capacityByMonth={calculationResult.capacity?.byTeamMonthly}
              />
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-400">
                <div className="text-center">
                  <Play className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Run simulation to see results</p>
                </div>
              </div>
            )}
          </div>

          {/* Bottleneck Table */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-gray-900">Bottleneck Analysis</h3>
            </div>
            <BottleneckTable bottlenecks={calculationResult?.bottlenecks} />
          </div>

          {/* Overloaded Projects */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-red-600" />
              <h3 className="font-semibold text-gray-900">Overloaded Projects</h3>
            </div>
            <OverloadedProjectsList 
              projects={calculationResult?.overloadedProjects}
              onExclude={handleExcludeProject}
            />
          </div>
        </div>

        {/* RIGHT PANEL: AI Recommendations */}
        <div className="col-span-3 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <AIRecommendationPanel
              scenarioId={selectedScenarioId}
              tenantId={effectiveTenantId}
            />
          </div>

          {/* Compare Link */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <GitBranch className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-900">Compare Scenarios</span>
            </div>
            <button
              onClick={() => navigate('/scenarios/compare')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-purple-300 text-purple-600 rounded-lg hover:bg-purple-50"
              data-testid="compare-scenarios-btn"
            >
              <ExternalLink className="w-4 h-4" />
              Open Comparison View
            </button>
          </div>

          {/* Help */}
          <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">How to use:</p>
                <ol className="text-xs space-y-1 list-decimal list-inside">
                  <li>Include/exclude projects</li>
                  <li>Shift dates if needed</li>
                  <li>Click "Run Simulation"</li>
                  <li>Review bottlenecks</li>
                  <li>Save promising scenarios</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScenarioPlanningPage;
